import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { rp } from "@/lib/payroll";
import NumField from "@/components/NumField";

export const ItemsDialog = ({
  open,
  onOpenChange,
  title,
  presets = [],
  items: incoming,
  rowKey,
  onChange,
  tid,
}) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (rowKey) setItems(incoming?.length ? incoming : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  const commit = (next) => {
    setItems(next);
    onChange(next);
  };
  const patch = (i, p) => commit(items.map((x, idx) => (idx === i ? { ...x, ...p } : x)));
  const total = items.reduce((a, p) => a + (Number(p.nominal) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={tid.dialog}
        className="max-h-[90vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-[19px] font-semibold tracking-tight">{title}</DialogTitle>
        </DialogHeader>

        {presets.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.key}
                data-testid={tid.preset(p.key)}
                onClick={() => commit([...items, { keterangan: p.label, nominal: 0 }])}
                className="rounded-full bg-[#F2F2F7] px-3 py-1.5 text-[13px] font-medium text-[#1D1D1F] transition-colors hover:bg-[#E7E7EC]"
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex min-w-0 items-center gap-2">
              <input
                data-testid={tid.keterangan(i)}
                value={it.keterangan}
                onChange={(e) => patch(i, { keterangan: e.target.value })}
                placeholder="Note"
                className="focus-ring h-10 w-full min-w-0 flex-1 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-[14px] placeholder:text-[#B0B0B8]"
              />
              <NumField
                testid={tid.nominal(i)}
                value={it.nominal}
                onChange={(v) => patch(i, { nominal: v })}
                className="h-10 w-24 shrink-0 text-[14px] sm:w-28"
              />
              <button
                data-testid={tid.remove(i)}
                onClick={() => commit(items.filter((_, idx) => idx !== i))}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E]"
                aria-label="Remove row"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <button
          data-testid={tid.add}
          onClick={() => commit([...items, { keterangan: "", nominal: 0 }])}
          className="mt-3 flex items-center gap-1.5 text-[13.5px] font-medium text-[#0071E3] transition-opacity hover:opacity-70"
        >
          <Plus className="h-4 w-4" /> Add row
        </button>

        <div className="mt-5 flex items-center justify-between border-t border-black/[0.06] pt-4">
          <span className="label">Total</span>
          <span data-testid={tid.total} className="num text-[17px] font-semibold">
            Rp {rp(total)}
          </span>
        </div>

        <button
          data-testid={tid.done}
          onClick={() => onOpenChange(false)}
          className="mt-4 h-11 w-full rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99]"
        >
          Done
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ItemsDialog;
